import React, { useEffect, useRef, useState } from "react";
import { X, Share2, Loader2, Sparkles, CheckCircle2, XCircle, ExternalLink, Type, Image as ImageIcon, Video, Upload, HardDrive } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { mediaSrc } from "./MessageExtras";
import { openGooglePicker } from "./DrivePicker";

// Listens for `oryntix:publish` — fired by the "Publikasikan" button on any media (publish an existing asset),
// or by "Posting baru" on the Social Media page (compose: pick type + upload/Drive media, then publish).
export function PublishHost() {
  const [job, setJob] = useState(null);
  useEffect(() => { const h = (e) => setJob(e.detail); window.addEventListener("oryntix:publish", h); return () => window.removeEventListener("oryntix:publish", h); }, []);
  if (!job) return null;
  return <PublishDialog media={job.media} context={job.context} compose={job.compose} onClose={() => setJob(null)} />;
}

const TYPES = [["text", "Teks", Type], ["image", "Gambar", ImageIcon], ["video", "Video", Video]];

export function PublishDialog({ media: initialMedia, context = "", compose = false, onClose }) {
  const [media, setMedia] = useState(initialMedia || null);
  const [type, setType] = useState(initialMedia ? (initialMedia.type === "video" ? "video" : "image") : "text");
  const kind = compose ? type : (media ? (media.type === "video" ? "video" : "image") : "text");
  const [accounts, setAccounts] = useState(null);
  const [picked, setPicked] = useState([]);
  const [caption, setCaption] = useState("");
  const [busy, setBusy] = useState("");
  const [results, setResults] = useState(null);
  const [driveOn, setDriveOn] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    api.get("/social/accounts").then((r) => {
      const ok = r.data.items.filter((a) => a.connected && a.kinds.includes(kind));
      setAccounts(r.data.items); setPicked(ok.slice(0, 1).map((a) => a.id));
    }).catch(() => setAccounts([]));
  }, [kind]);
  useEffect(() => { if (compose) api.get("/integrations/google/status").then((r) => setDriveOn(!!r.data.picker)).catch(() => {}); }, [compose]);

  const changeType = (t) => {
    setType(t); setResults(null);
    if (t === "text") setMedia(null);
    else if (media && ((t === "video") !== (media.type === "video"))) setMedia(null);
  };

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy("upload");
    try {
      const fd = new FormData(); fd.append("file", f); fd.append("kind", type);
      const r = await api.post("/social/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setMedia({ type, path: r.data.media_path, name: r.data.name });
    } catch (err) { toast.error(err?.response?.data?.detail || "Gagal mengunggah"); }
    finally { setBusy(""); if (fileRef.current) fileRef.current.value = ""; }
  };
  const fromDrive = async () => {
    setBusy("drive");
    try {
      const items = await openGooglePicker({ max: 1 });
      const it = items[0];
      if (it) setMedia({ type, drive_id: it.drive_id, name: it.name, link: it.link });
    } catch (err) { toast.error(err?.response?.data?.detail || "Google Picker gagal dibuka"); }
    finally { setBusy(""); }
  };

  const genCaption = async () => {
    setBusy("caption");
    try { const r = await api.post("/social/caption", { context: context || "", providers: picked, kind, media_path: media?.path || null, drive_id: media?.drive_id || null }); setCaption(r.data.caption); }
    catch (e) { toast.error("Gagal membuat caption"); } finally { setBusy(""); }
  };
  const send = async () => {
    if (!picked.length) { toast.error("Pilih minimal satu akun"); return; }
    if (kind !== "text" && !media) { toast.error("Pilih atau unggah media dulu"); return; }
    if (kind === "text" && !caption.trim()) { toast.error("Tulis teks yang akan diposting"); return; }
    setBusy("send");
    try {
      const r = await api.post("/social/publish", { providers: picked, kind, text: caption, title: media?.name || "", media_path: media?.path || null, drive_id: media?.drive_id || null, app_url: window.location.origin });
      setResults(r.data.results);
      if (r.data.results.every((x) => x.status === "sent")) toast.success("Terkirim ke media sosial");
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal memposting"); } finally { setBusy(""); }
  };
  const eligible = (accounts || []).filter((a) => a.kinds.includes(kind));
  const needsMedia = kind !== "text";
  return (
    <div className="fixed inset-0 z-[96] flex items-center justify-center p-4" data-testid="publish-dialog">
      <div className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative flex max-h-[88vh] w-full max-w-lg flex-col overflow-hidden rounded-3xl border border-[#E7ECF3] bg-white shadow-2xl fade-up">
        <div className="flex items-center justify-between border-b border-[#E7ECF3] px-5 py-4">
          <p className="flex items-center gap-2 text-sm font-bold text-slate-900"><Share2 size={16} className="text-[#2F6BFF]" /> {compose ? "Buat posting baru" : "Publikasikan ke media sosial"}</p>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="publish-close"><X size={18} /></button>
        </div>
        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-5">
          {compose && (
            <div className="flex gap-2" data-testid="publish-type-tabs">
              {TYPES.map(([k, l, I]) => (
                <button key={k} type="button" onClick={() => changeType(k)} data-testid={`publish-type-${k}`}
                  className={`flex flex-1 items-center justify-center gap-1.5 rounded-xl border px-3 py-2 text-xs font-bold transition ${type === k ? "border-[#2F6BFF] bg-[#EEF3FF] text-[#2F6BFF]" : "border-[#E7ECF3] text-slate-600 hover:bg-slate-50"}`}>
                  <I size={14} /> {l}
                </button>
              ))}
            </div>
          )}
          {needsMedia && media ? (
            <div className="relative">
              {media.type === "video" ? <video src={mediaSrc(media)} className="max-h-48 w-full rounded-xl bg-black" controls data-testid="publish-media-preview" /> : <img src={mediaSrc(media)} alt="" className="max-h-48 w-full rounded-xl object-cover" data-testid="publish-media-preview" />}
              <p className="mt-1 truncate text-xs text-slate-500">{media.name}</p>
              {compose && <button type="button" onClick={() => setMedia(null)} className="absolute right-2 top-2 flex items-center gap-1 rounded-full bg-black/60 px-2.5 py-1 text-[11px] font-semibold text-white hover:bg-black/80" data-testid="publish-media-clear"><X size={11} /> Ganti</button>}
            </div>
          ) : needsMedia && compose ? (
            <div className="rounded-2xl border border-dashed border-[#CBD6E6] bg-[#F7F9FC] p-5 text-center" data-testid="publish-media-picker">
              <p className="text-xs font-semibold text-slate-600">Tambahkan {type === "video" ? "video" : "gambar"} untuk diposting</p>
              <input ref={fileRef} type="file" accept={type === "video" ? "video/*" : "image/*"} onChange={upload} className="hidden" data-testid="publish-upload-input" />
              <div className="mt-3 flex flex-wrap items-center justify-center gap-2">
                <button type="button" onClick={() => fileRef.current?.click()} disabled={!!busy} className="btn-grad flex items-center gap-1.5 rounded-xl px-4 py-2 text-xs font-bold disabled:opacity-50" data-testid="publish-upload-btn">{busy === "upload" ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />} Unggah dari perangkat</button>
                {driveOn && <button type="button" onClick={fromDrive} disabled={!!busy} className="flex items-center gap-1.5 rounded-xl border border-[#2F6BFF] px-4 py-2 text-xs font-bold text-[#2F6BFF] hover:bg-[#EEF3FF] disabled:opacity-50" data-testid="publish-drive-btn">{busy === "drive" ? <Loader2 size={13} className="animate-spin" /> : <HardDrive size={13} />} Ambil dari Google Drive</button>}
              </div>
              {!driveOn && <p className="mt-2 text-[11px] text-slate-400">Hubungkan Google Drive di menu Integrasi untuk mengambil media dari Drive.</p>}
            </div>
          ) : null}
          <div>
            <p className="mb-1.5 text-[11px] font-bold uppercase tracking-wider text-slate-400">Akun tujuan</p>
            {accounts === null ? <Loader2 size={14} className="animate-spin text-slate-400" /> : eligible.length === 0 ? <p className="text-xs text-slate-500">Belum ada akun yang mendukung {kind}. <a href="/social" className="font-semibold text-[#2F6BFF]">Hubungkan di menu Social Media →</a></p> : (
              <div className="flex flex-wrap gap-2">{eligible.map((a) => (
                <button key={a.id} type="button" disabled={!a.connected} onClick={() => setPicked((p) => p.includes(a.id) ? p.filter((x) => x !== a.id) : [...p, a.id])} data-testid={`publish-acc-${a.id}`}
                  className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition disabled:opacity-40 ${picked.includes(a.id) ? "border-[#2F6BFF] bg-[#EEF3FF] text-[#2F6BFF]" : "border-[#E7ECF3] text-slate-600"}`}>{a.label}{a.account_name ? ` · ${a.account_name}` : a.connected ? "" : " (belum terhubung)"}</button>
              ))}</div>
            )}
          </div>
          <div>
            <div className="mb-1.5 flex items-center justify-between"><p className="text-[11px] font-bold uppercase tracking-wider text-slate-400">{kind === "text" ? "Teks posting" : "Caption"}</p><button onClick={genCaption} disabled={!!busy} className="flex items-center gap-1 text-xs font-semibold text-[#2F6BFF]" data-testid="publish-gen-caption">{busy === "caption" ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />} Tulis otomatis oleh asisten</button></div>
            <textarea value={caption} onChange={(e) => setCaption(e.target.value)} rows={4} placeholder={kind === "text" ? "Tulis isi posting Anda…" : "Tulis caption, atau minta asisten menulisnya…"} className="input-dark w-full resize-none py-2.5 text-sm" data-testid="publish-caption" />
          </div>
          {results && (
            <div className="space-y-1.5 rounded-xl border border-[#E7ECF3] p-3 text-xs" data-testid="publish-results">
              {results.map((r) => <p key={r.id} className="flex items-center gap-2">{r.status === "sent" ? <CheckCircle2 size={13} className="text-emerald-600" /> : <XCircle size={13} className="text-rose-600" />}<b>{r.provider}</b>{r.status === "sent" ? <a href={r.post_url} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-[#2F6BFF]">lihat post <ExternalLink size={10} /></a> : <span className="text-rose-600">{r.error}</span>}</p>)}
            </div>
          )}
        </div>
        <div className="flex items-center justify-end gap-2 border-t border-[#E7ECF3] px-5 py-3">
          <button onClick={onClose} className="rounded-xl px-4 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-100" data-testid="publish-cancel">{results ? "Tutup" : "Batal"}</button>
          {!results && <button onClick={send} disabled={!!busy || !picked.length || (needsMedia && !media)} className="btn-grad flex items-center gap-1.5 rounded-xl px-4 py-2 text-xs font-bold disabled:opacity-50" data-testid="publish-send">{busy === "send" ? <Loader2 size={13} className="animate-spin" /> : <Share2 size={13} />} Publikasikan</button>}
        </div>
      </div>
    </div>
  );
}
